import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { adminApi } from '../../lib/api';
import { 
  UserCheck, UserX, Clock, CheckCircle, XCircle, Loader2, 
  Mail, Calendar, RefreshCw, Users, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const AdminUserApproval = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeStats, setActiveStats] = useState({ total_active: 0, total_idle: 0 });
  const [loading, setLoading] = useState(true);
  
  // Reject modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [userToReject, setUserToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchPendingUsers = useCallback(async () => {
    try {
      const response = await adminApi.getPendingUsers();
      setPendingUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load pending users');
    }
  }, []);

  const fetchApprovedUsers = useCallback(async () => {
    try {
      const response = await adminApi.getApprovedUsers({ limit: 50 });
      setApprovedUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load approved users');
    }
  }, []);

  const fetchRejectedUsers = useCallback(async () => {
    try {
      const response = await adminApi.getRejectedUsers();
      setRejectedUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load rejected users');
    }
  }, []);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const response = await adminApi.getActiveUsers();
      setActiveUsers(response.data.users || []);
      setActiveStats({
        total_active: response.data.total_active || 0,
        total_idle: response.data.total_idle || 0
      });
    } catch (error) {
      toast.error('Failed to load active users');
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchPendingUsers(),
      fetchApprovedUsers(),
      fetchRejectedUsers(),
      fetchActiveUsers()
    ]);
    setLoading(false);
  }, [fetchPendingUsers, fetchApprovedUsers, fetchRejectedUsers, fetchActiveUsers]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh active users every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'active') {
        fetchActiveUsers();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTab, fetchActiveUsers]);

  const handleApprove = async (userId, userName) => {
    setProcessing(true);
    try {
      await adminApi.approveUser(userId);
      toast.success(`${userName} has been approved`);
      fetchPendingUsers();
      fetchApprovedUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = (user) => {
    setUserToReject(user);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!userToReject) return;
    
    setProcessing(true);
    try {
      await adminApi.rejectUser(userToReject.user_id, rejectionReason);
      toast.success(`${userToReject.name} has been rejected`);
      setRejectModalOpen(false);
      fetchPendingUsers();
      fetchRejectedUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const UserCard = ({ user, showActions = false, showStatus = false }) => (
    <Card className="hover:shadow-md transition-shadow" data-testid={`user-card-${user.user_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center">
              <span className="text-[#4A9CC7] font-bold text-lg">{user.name?.charAt(0) || 'U'}</span>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{user.name}</p>
              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                <Mail className="w-3 h-3" />
                {user.email}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                <Calendar className="w-3 h-3" />
                Registered: {formatDate(user.created_at)}
              </div>
            </div>
          </div>
          
          {showStatus && (
            <Badge 
              variant={user.approval_status === 'approved' ? 'default' : 'destructive'}
              className="capitalize"
            >
              {user.approval_status}
            </Badge>
          )}
        </div>
        
        {user.rejection_reason && (
          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <strong>Rejection Reason:</strong> {user.rejection_reason}
            </p>
          </div>
        )}
        
        {showActions && (
          <div className="flex gap-2 mt-4">
            <Button 
              onClick={() => handleApprove(user.user_id, user.name)}
              disabled={processing}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid={`approve-btn-${user.user_id}`}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
            <Button 
              onClick={() => openRejectModal(user)}
              disabled={processing}
              variant="destructive"
              className="flex-1"
              data-testid={`reject-btn-${user.user_id}`}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const ActiveUserRow = ({ user }) => (
    <tr className="hover:bg-gray-50 dark:hover:bg-slate-800/50" data-testid={`active-user-${user.user_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center">
            <span className="text-[#4A9CC7] font-medium">{user.name?.charAt(0) || 'U'}</span>
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {formatDate(user.login_time)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {formatDate(user.last_activity)}
      </td>
      <td className="px-4 py-3">
        <Badge 
          variant={user.status === 'active' ? 'default' : 'secondary'}
          className={user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}
        >
          <span className={`w-2 h-2 rounded-full mr-1 ${user.status === 'active' ? 'bg-green-200' : 'bg-gray-300'}`}></span>
          {user.status === 'active' ? 'Active' : 'Idle'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {user.session_duration || '-'}
      </td>
    </tr>
  );

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-user-approval-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope' }}>
              User Approval & Monitoring
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Manage user registrations and monitor active sessions</p>
          </div>
          <Button 
            onClick={fetchAllData}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 w-full md:w-auto">
            <TabsTrigger value="pending" className="flex items-center gap-2" data-testid="tab-pending">
              <Clock className="w-4 h-4" />
              Pending
              {pendingUsers.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendingUsers.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" className="flex items-center gap-2" data-testid="tab-approved">
              <UserCheck className="w-4 h-4" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2" data-testid="tab-rejected">
              <UserX className="w-4 h-4" />
              Rejected
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-2" data-testid="tab-active">
              <Activity className="w-4 h-4" />
              Active Sessions
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#4A9CC7]" />
            </div>
          ) : (
            <>
              <TabsContent value="pending">
                {pendingUsers.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Clock className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">No pending approval requests</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {pendingUsers.map(user => (
                      <UserCard key={user.user_id} user={user} showActions />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="approved">
                {approvedUsers.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <UserCheck className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">No approved users yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {approvedUsers.map(user => (
                      <UserCard key={user.user_id} user={user} showStatus />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="rejected">
                {rejectedUsers.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <UserX className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">No rejected users</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {rejectedUsers.map(user => (
                      <UserCard key={user.user_id} user={user} showStatus />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="active">
                <div className="grid gap-4 md:grid-cols-3 mb-6">
                  <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-500/15 flex items-center justify-center">
                        <Activity className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeStats.total_active}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Active Users</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeStats.total_idle}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Idle Users</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {activeStats.total_active + activeStats.total_idle}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Sessions</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {activeUsers.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Activity className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">No active sessions</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-slate-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Login Time</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Activity</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duration</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                            {activeUsers.map(user => (
                              <ActiveUserRow key={user.user_id} user={user} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Reject Modal */}
        <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                Reject User: {userToReject?.name}
              </DialogTitle>
              <DialogDescription>
                This user will not be able to access the application. You can optionally provide a reason.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Rejection Reason (Optional)</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter a reason for rejection..."
                className="mt-1"
                rows={3}
                data-testid="rejection-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={processing}
                data-testid="confirm-reject-btn"
              >
                {processing ? 'Rejecting...' : 'Reject User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUserApproval;
